import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedDate, FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import type { Position } from '#src/__generated__/globalTypes.ts';
import { Sport } from '#src/__generated__/globalTypes.ts';
import { ButtonBase } from '#src/atoms/buttons/ButtonBase/index.tsx';
import { Horizontal, Vertical } from '#src/atoms/layout/flex/index.tsx';
import { BodyM, BrandXS } from '#src/atoms/typography/index.tsx';
import type { LineupPosition } from '#src/components/HowToPlay/CreateATeam/constants.ts';
import { BonusChip } from '#src/components/card/BonusChip/index.tsx';
import { CardImg } from '#src/components/card/CardImg.tsx';
import { StatsIcon as DumbStatsIcon } from '#src/components/draft/StatsIcon/index.tsx';
import { PositionIcon } from '#src/components/lineup/PositionIcon/index.tsx';
import { Cost } from '#src/components/player/Cost/index.tsx';
import { fantasy } from '#src/lib/glossary.ts';
import { normalizeStatsFromRawScores } from '#src/lib/performance.tsx';
import { laptopAndAbove } from '#src/style/mediaQuery.ts';

import type { PlayerInformation_anyPlayer } from './__generated__/index.graphql.ts';

const Root = styled(ButtonBase)`
  display: flex;
  width: 100%;
`;

const PlayerContainer = styled(Horizontal)`
  justify-content: space-between;
  width: 100%;
  background: var(--c-nd-50);
  padding: var(--half-unit) var(--double-unit) var(--half-unit) var(--half-unit);
  border-radius: var(--unit);
`;

const SelectCardImgWrapper = styled.div`
  width: 80px;
  height: auto;
`;

const GamesPlayedContainer = styled(Horizontal).attrs({ center: true })`
  padding: var(--half-unit);
  background-color: var(--c-score-high);
  border-radius: var(--half-unit);
`;

const StatsCostContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--unit);
  @media ${laptopAndAbove} {
    flex-direction: row;
  }
`;

type Props = {
  isPending: boolean;
  startTransition: (callback: () => void) => void;
  position: LineupPosition | null;
  playerInfo: any;
  selectedPlayers: {
    [key in Position]?: string | null;
  };
  setSelectedPlayers: (players: {
    [key in Position]?: string | null;
  }) => void;
};

export const PlayerInformation = ({
  isPending,
  startTransition,
  position,
  playerInfo,
  selectedPlayers,
  setSelectedPlayers,
}: Props) => {
  const stats = normalizeStatsFromRawScores(
    playerInfo?.anyPlayer.rawPlayerGameScores || [],
    playerInfo?.anyCard?.sport || Sport.FOOTBALL,
    15
  );

  return (
    <Root
      style={{ opacity: isPending ? 0.5 : 1 }}
      onClick={() =>
        startTransition(() => {
          setSelectedPlayers({
            ...selectedPlayers,
            [position as LineupPosition]: playerInfo?.anyCard?.pictureUrl,
          });
        })
      }
    >
      <PlayerContainer>
        <Horizontal>
          <SelectCardImgWrapper>
            <CardImg src={playerInfo?.anyCard?.pictureUrl} width={80} />
          </SelectCardImgWrapper>
          <Vertical>
            <Horizontal>
              <BonusChip
                bonus={Number(playerInfo?.anyCard?.power)}
                withTransferMalus={false}
                transferMalus={0}
              />
              <GamesPlayedContainer>
                <PositionIcon
                  position={playerInfo?.anyPlayer.anyPositions[0]}
                />
                <BrandXS color="var(--c-black)">
                  {Math.round(
                    Number(
                      playerInfo?.anyPlayer.anySo5AverageLastScore
                        ?.percentageOfGamesPlayed ?? 100
                    )
                  )}
                  %
                </BrandXS>
              </GamesPlayedContainer>
            </Horizontal>
            <Horizontal>
              <img
                src={
                  playerInfo?.anyPlayer.anyGameStats[0]?.anyGame?.homeTeam
                    ?.pictureSecondaryUrl
                }
                alt=""
                width={30}
                height={30}
              />
              <BrandXS uppercase bold>
                <FormattedMessage {...fantasy.vs} />
              </BrandXS>
              <img
                src={
                  playerInfo?.anyPlayer.anyGameStats[0]?.anyGame?.awayTeam
                    ?.pictureSecondaryUrl
                }
                alt=""
                width={30}
                height={30}
              />
            </Horizontal>
            <BodyM color="var(--c-nd-600)">
              <FormattedDate
                value={playerInfo?.anyPlayer.anyGameStats[0]?.anyGame?.date}
                day="2-digit"
                month="long"
                timeZone="UTC"
              />
            </BodyM>
          </Vertical>
        </Horizontal>
        <StatsCostContainer>
          <DumbStatsIcon stats={stats} width={10} height={4} />
          <Cost score={playerInfo?.anyPlayer.seasonAverage} />
        </StatsCostContainer>
      </PlayerContainer>
    </Root>
  );
};

PlayerInformation.fragments = {
  anyPlayer: gql`
    fragment PlayerInformation_anyPlayer on AnyPlayerInterface {
      slug
      sport
      anyPositions
      rawPlayerGameScores(last: 15)
      anyGameStats(last: 1) {
        id
        anyGame {
          date
          id
          homeTeam {
            slug
            pictureSecondaryUrl
          }
          awayTeam {
            slug
            pictureSecondaryUrl
          }
          so5Fixture {
            slug
            shortDisplayName
          }
        }
      }
      seasonAverage: averageScore(type: SEASON_AVERAGE_SCORE)
    }
  ` as TypedDocumentNode<PlayerInformation_anyPlayer>,
};
